/**
 * form/form.ts — Form class: orchestrates inputs, tab navigation, and lifecycle.
 *
 * Render (multi-question):
 *   ─────────────────────────────────── (hr accent)
 *   ≺ · Q1 │ ✔ Q2 │ ✦ Q3 │ ≡ Review ≻ (tabs)
 *
 *    Question en gras ?                 (question)
 *    Description en dim                 (description, max 4 lines)
 *
 *   <input.renderWidget()>              (widget delegated to input)
 *
 *    ✘ Error message                    (validation error if present)
 *
 *    Enter validate · ↑/↓ navigate      (footer hints from input)
 *    Tab/Shift+Tab switch question      (footer hints from Tabs)
 *    Esc quit                           (always present)
 *   ─────────────────────────────────── (hr accent)
 *
 * Render (single-question):
 *   Same but without the tab bar.
 *
 * Keyboard chaining:
 *   1. Exit confirm dialog (if active) → Y/Enter=quit, other=dismiss
 *   2. Active input tries to handle → if consumed, refresh
 *   3. Tabs tries Tab/Shift+Tab → if consumed, stop
 *   4. Escape (unhandled) → show exit confirmation
 */

import type { Editor } from "@mariozechner/pi-tui";
import {
  Key,
  matchesKey,
  truncateToWidth,
  wrapTextWithAnsi,
} from "@mariozechner/pi-tui";
import type { Answer, FormResult, InputValue, Question } from "../types";
import type {
  Input,
  InputCallbacks,
  RenderContext,
  Theme,
} from "./inputs/types";
import type { FormQuestion } from "./question";
import { ReviewScreen } from "./review";
import { Tabs } from "./tabs";

/**
 * Default max visible option rows for scrollable input widgets.
 * Static for now; can later be derived from terminal / window height.
 */
const WIDGET_MAX_H = 4;

/**
 * Total lines passed to ReviewScreen.render() — sets the review frame height.
 * Chosen so that visibleCount = REVIEW_MAX_H − REVIEW_OVERHEAD = 15 − 7 = 8,
 * matching the previous hard-coded REVIEW_MAX_VISIBLE = 8 constant.
 * Can later be derived from terminal height.
 */
const REVIEW_MAX_H = 15;

/**
 * Orchestrates the interactive form: input lifecycle, tab navigation,
 * review screen, exit confirmation, and final answer collection.
 *
 * Responsibilities:
 *   - Activates/deactivates inputs on tab switches.
 *   - Delegates keyboard events to the active input, then to {@link Tabs}.
 *   - Renders the appropriate frame (input, review, or exit confirmation).
 *   - Calls `done` with a {@link FormResult} on submit or cancel.
 */
export class Form {
  private readonly _tabs: Tabs;
  private readonly _reviewScreen: ReviewScreen;
  private readonly _questions: FormQuestion[];
  private _showExitConfirm = false;
  /** Which option is highlighted in the exit dialog: true = Y (quit), false = N (continue). */
  private _exitSelection = true;
  private _cachedLines: string[] | undefined;
  /** Width used for the current `_cachedLines` — cache is invalidated on resize. */
  private _cachedWidth: number | undefined;

  /** `true` when the form contains more than one question (shows tab bar). */
  get isMulti(): boolean {
    return this._questions.length > 1;
  }

  /** The {@link FormQuestion} whose input is currently focused. */
  get activeQuestion(): FormQuestion {
    const question = this._questions[this._tabs.activeIndex];
    if (!question)
      throw new Error(`No question at index ${this._tabs.activeIndex}`);
    return question;
  }

  /**
   * @param questions       - Ordered list of questions with their inputs.
   * @param _editor         - Shared Editor instance (passed through to inputs).
   * @param _theme          - Active color theme.
   * @param _requestRender  - Callback that asks the TUI to repaint.
   * @param _done           - Callback invoked with the final {@link FormResult}.
   */
  constructor(
    questions: FormQuestion[],
    private readonly _editor: Editor,
    private readonly _theme: Theme,
    private readonly _requestRender: () => void,
    private readonly _done: (result: FormResult) => void,
  ) {
    this._questions = questions;
    this._reviewScreen = new ReviewScreen(questions, _theme);

    // Wire onTabChange to manage input lifecycle.
    this._tabs = new Tabs(
      questions,
      this.isMulti,
      (oldIdx, newIdx, isReview) => {
        // Deactivate old input.
        const oldQ = questions[oldIdx];
        if (oldQ) {
          this._deactivateInput(oldQ.input);
        }
        // Activate new input.
        const newQ = questions[newIdx];
        if (!isReview && newQ) {
          this._activateInput(newQ.input);
        }
        // Reset review scroll whenever the tab changes.
        this._reviewScreen.reset();
      },
      () => this._refresh(),
    );

    // Activate the first input.
    if (questions.length > 0) {
      this._activateInput(questions[0]!.input);
    }
  }

  /**
   * Main keypress handler — called by the TUI for every keystroke.
   *
   * Delegation order:
   *  1. Exit confirmation dialog (if active)
   *  2. Active input widget
   *  3. Review screen (Enter submits, ↑/↓ scroll)
   *  4. Tabs (Tab / Shift+Tab)
   *  5. Escape → show exit confirmation
   *
   * @param data - Raw terminal key event (e.g. `"\r"`, `"\x1b[A"`, `"a"`).
   */
  handleInput(data: string): void {
    // 1. Exit confirmation dialog.
    if (this._showExitConfirm) {
      if (matchesKey(data, Key.left) || matchesKey(data, Key.right)) {
        this._exitSelection = !this._exitSelection;
        this._refresh();
        return;
      }
      if (data === "y" || data === "Y") {
        this._collectAndFinish(true);
        return;
      }
      if (data === "n" || data === "N") {
        this._showExitConfirm = false;
        this._exitSelection = true;
        this._refresh();
        return;
      }
      if (matchesKey(data, Key.enter)) {
        if (this._exitSelection) {
          this._collectAndFinish(true);
        } else {
          this._showExitConfirm = false;
          this._exitSelection = true;
          this._refresh();
        }
        return;
      }
      // Any other key: dismiss.
      this._showExitConfirm = false;
      this._exitSelection = true;
      this._refresh();
      return;
    }

    // 2. Delegate to active input (if not on review).
    if (!this._tabs.isOnReview) {
      if (this.activeQuestion.input.handleInput(data)) {
        this._refresh();
        return;
      }
    }

    // 3. Review screen — delegate Enter/↑/↓; Tab/Shift+Tab fall through to Tabs.
    if (this._tabs.isOnReview) {
      const action = this._reviewScreen.handleInput(data);
      if (action === "submit") {
        this._collectAndFinish(false);
        return;
      }
      if (action === "scrolled") {
        this._refresh();
        return;
      }
      // null → not consumed; fall through to Tabs.
    }

    // 4. Tabs handles Tab/Shift+Tab.
    if (this.isMulti && this._tabs.handleInput(data)) {
      return; // Tabs manages its own refresh.
    }

    // 5. Escape (not consumed by anyone) → exit confirmation.
    if (matchesKey(data, Key.escape)) {
      this._showExitConfirm = true;
      this._refresh();
      return;
    }
  }

  /**
   * Render the form frame. Returns cached lines when state has not changed at the same width.
   *
   * @param width - Terminal width in columns.
   * @returns Array of rendered lines (one string per row).
   */
  render(width: number): string[] {
    if (this._cachedLines && this._cachedWidth === width)
      return this._cachedLines;

    const lines: string[] = [];
    const maxW = width;
    const add = (s: string) => lines.push(truncateToWidth(s, maxW));
    const hr = () => add(this._theme.fg("accent", "─".repeat(maxW)));

    hr();

    // Tabs (if multi-question).
    if (this.isMulti) {
      add(this._tabs.render(this._theme, maxW));
      lines.push("");
    }

    if (this._showExitConfirm) {
      lines.push(...this._renderExitConfirm(maxW));
    } else if (this._tabs.isOnReview) {
      lines.push(...this._reviewScreen.render(maxW, REVIEW_MAX_H));
    } else {
      lines.push(...this._renderInputFrame(this._tabs.activeIndex, maxW));
    }

    hr();
    this._cachedWidth = width;
    this._cachedLines = lines;
    return lines;
  }

  /** Clears the render cache and requests a repaint. */
  invalidate(): void {
    this._cachedLines = undefined;
    this._cachedWidth = undefined;
    this._requestRender();
  }

  // ── Input lifecycle ───────────────────────────────────────────────────────────

  /**
   * Duck-typed call to `input.activate()` when it exists (TextInput).
   * Other inputs have no lifecycle hooks.
   */
  private _activateInput(input: Input): void {
    if ("activate" in input && typeof input.activate === "function") {
      (input as { activate(): void }).activate();
    }
  }

  /** Duck-typed call to `input.deactivate()` — saves in-progress editor text. */
  private _deactivateInput(input: Input): void {
    if ("deactivate" in input && typeof input.deactivate === "function") {
      (input as { deactivate(): void }).deactivate();
    }
  }

  // ── Input frame rendering ─────────────────────────────────────────────────────

  /**
   * Renders the full frame for a question: question + description + widget + error + footer.
   */
  private _renderInputFrame(idx: number, maxW: number): string[] {
    const fq = this._questions[idx]!;
    const input = fq.input;
    const { _theme: theme } = this;
    const lines: string[] = [];
    const add = (s: string) => lines.push(truncateToWidth(s, maxW));

    // Question.
    lines.push("");
    add(` ${theme.bold(theme.fg("text", fq.question))}`);

    // Description (max 4 lines, word-wrapped to fit terminal width).
    if (fq.description) {
      const descLines = wrapTextWithAnsi(fq.description, Math.max(1, maxW - 1));
      for (const dline of descLines.slice(0, 4)) {
        add(` ${theme.fg("dim", dline)}`);
      }
    }

    lines.push("");

    // Widget (delegated to input).
    const ctx: RenderContext = {
      theme: this._theme,
      editor: this._editor,
      maxW,
      maxH: WIDGET_MAX_H,
    };
    lines.push(...input.renderWidget(ctx));

    // Validation error.
    const err = input.getValidationError();
    if (err) {
      lines.push("");
      add(` ${theme.fg("error", `✘ ${err}`)}`);
    }

    // Footer hints.
    lines.push(...this._renderFooterHints(input, maxW));

    return lines;
  }

  // ── Footer hints ──────────────────────────────────────────────────────────────

  /**
   * Builds the footer hint line from the active input's hints plus global shortcuts.
   *
   * @param input - The currently active input (provides its own hint entries).
   * @param maxW  - Maximum rendered width in columns.
   * @returns Two lines: an empty separator and the formatted hint string.
   */
  private _renderFooterHints(input: Input, maxW: number): string[] {
    const { _theme: theme } = this;
    const hints = input.getFooterHints();
    const parts: string[] = [];

    for (const h of hints) {
      const keyStr = h.keys.map((k) => formatKeyId(k)).join("/");
      parts.push(`${keyStr} ${h.action}`);
    }

    if (this.isMulti) {
      parts.push("Tab/Shift+Tab switch question");
    }

    parts.push("Esc quit");

    return [
      "",
      truncateToWidth(` ${theme.fg("dim", parts.join(" · "))}`, maxW),
    ];
  }

  // ── Exit confirmation ─────────────────────────────────────────────────────────

  /**
   * Renders the "[Y]es / [N]o" exit confirmation dialog.
   *
   * @param maxW - Maximum rendered width in columns.
   * @returns Lines for the confirmation prompt.
   */
  private _renderExitConfirm(maxW: number): string[] {
    const { _theme: theme } = this;
    const lines: string[] = [];
    const add = (s: string) => lines.push(truncateToWidth(s, maxW));

    lines.push("");
    add(` ${theme.fg("warning", "⚠ Are you sure you want to quit?")}`);
    add(`   ${theme.fg("dim", "All answers will be lost.")}`);
    lines.push("");
    const yStyled = this._exitSelection
      ? theme.bold(theme.fg("text", "[Y]es, quit"))
      : theme.fg("dim", "[Y]es, quit");
    const nStyled = !this._exitSelection
      ? theme.bold(theme.fg("text", "[N]o, continue"))
      : theme.fg("dim", "[N]o, continue");
    add(` ${yStyled}  ${theme.fg("dim", "·")}  ${nStyled}`);

    return lines;
  }

  // ── Review screen ─────────────────────────────────────────────────────────────

  // ── Collect and finish ────────────────────────────────────────────────────────

  /**
   * Dispose all inputs, collect typed answers from each FormQuestion,
   * and invoke the `done` callback. Called on submit or cancel.
   *
   * Each {@link Answer} carries:
   *   - `questionId` — the 0-based index of the question in the original array.
   *   - `value`      — the typed answer, or `null` if the question was skipped.
   */
  private _collectAndFinish(cancelled: boolean): void {
    // Only deactivate the currently active input — previously deactivated inputs
    // already have their values committed. Re-deactivating them would overwrite
    // those saved values with whatever the shared Editor currently holds (which
    // is the last active input's text, potentially empty or belonging to a
    // different question), causing all prior answers to appear as null.
    if (!this._tabs.isOnReview) {
      const activeQ = this._questions[this._tabs.activeIndex];
      if (activeQ) {
        this._deactivateInput(activeQ.input);
      }
    }

    for (const fq of this._questions) {
      fq.input.dispose();
    }

    const answers: Answer[] = this._questions.map((fq, idx) => ({
      questionId: fq.questionIdx ?? idx,
      value: fq.input.isAnswered()
        ? (fq.input.getTypedValue() as InputValue)
        : null,
    }));

    const questions = this._questions.map(
      (fq) =>
        fq.originalQuestion ?? {
          id: fq.question,
          type: fq.input.type,
          question: fq.question,
          header: fq.header,
        },
    ) as Question[];

    this._done({ questions, answers, cancelled });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  /** Clears the render cache and requests a repaint. */
  private _refresh(): void {
    this._cachedLines = undefined;
    this._cachedWidth = undefined;
    this._requestRender();
  }

  /**
   * Creates an {@link InputCallbacks} object wired to this Form's navigation.
   *
   * `onAdvance` advances or submits (single-question), `onRetreat` retreats,
   * `onSubmit` submits unconditionally, and `onRefresh` requests a repaint.
   *
   * @returns The callbacks object to inject into each Input at construction time.
   */
  createCallbacks(): InputCallbacks {
    return {
      onAdvance: () => {
        if (this.isMulti) {
          this._tabs.advance();
        } else {
          this._collectAndFinish(false);
        }
      },
      onRetreat: () => {
        if (this.isMulti) {
          this._tabs.retreat();
        }
      },
      onSubmit: () => {
        this._collectAndFinish(false);
      },
      onRefresh: () => {
        this._refresh();
      },
    };
  }
}

/**
 * Converts a pi-tui {@link KeyId} to a human-readable string.
 *
 * @example
 * formatKeyId("up")        // "↑"
 * formatKeyId("shift+tab") // "Shift+Tab"
 */
function formatKeyId(keyId: string): string {
  const map: Record<string, string> = {
    up: "↑",
    down: "↓",
    left: "←",
    right: "→",
    enter: "Enter",
    escape: "Esc",
    tab: "Tab",
    space: "Space",
    backspace: "⌫",
  };
  // Handle modified keys like "shift+tab".
  if (keyId.includes("+")) {
    const parts = keyId.split("+");
    const base = parts[parts.length - 1]!;
    const modifiers = parts.slice(0, -1);
    const formattedBase = map[base] ?? base;
    return [
      ...modifiers.map((m) => m.charAt(0).toUpperCase() + m.slice(1)),
      formattedBase,
    ].join("+");
  }
  return map[keyId] ?? keyId;
}
